import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { Suspense } from "react";

import { UnsupportedScreenModal } from "../../../features/DragonTiles/components/UnsupportedScreenModal";
import { useScreenSize } from "../../../features/DragonTiles/hooks/useScreenSize";
import type { QueryClient } from "@tanstack/react-query";

import {
  dragonTilesByTypeQueryOptions,
  dragonTilesQueryOptions,
} from "@/api/dragon-tiles";
import { allStepsDataQueryOptions } from "@/api/onboarding";
import { LoadingFallback } from "@/components/LoadingFallback";
import {
  allOnboardingStepsComplete,
  getFirstIncompleteStep,
} from "@/features/Onboarding/utils/steps";
import { useHasElevatedAccess } from "@/utils/privilegedMode";

export const Route = createFileRoute("/_auth/dragon-tiles/$siteId")({
  beforeLoad: async ({ context, params }) => {
    // Skip onboarding check in development mode
    if (import.meta.env.DEV) {
      return;
    }
    const { queryClient } = context as { queryClient: QueryClient };
    const { siteId } = params;
    if (siteId) {
      const setupData = await queryClient.ensureQueryData(
        allStepsDataQueryOptions(siteId),
      );
      if (!allOnboardingStepsComplete(setupData)) {
        const firstIncompleteStep = getFirstIncompleteStep(setupData);

        throw redirect({
          to: "/brand-onboarding/$siteId/$stepId",
          params: { siteId, stepId: firstIncompleteStep },
        });
      }
    }
  },
  component: RouteComponent,
  loader: async ({ context, params, location }) => {
    const { queryClient } = context as { queryClient: QueryClient };
    const { siteId } = params;
    const connectorsBasePath = `/dragon-tiles/${siteId}/connectors`;
    const isConnectorsRoute =
      location.pathname === connectorsBasePath ||
      location.pathname.startsWith(`${connectorsBasePath}/`);

    if (isConnectorsRoute) {
      await queryClient.ensureQueryData(
        dragonTilesByTypeQueryOptions(siteId, "connector"),
      );
      return;
    }

    await queryClient.ensureQueryData(dragonTilesQueryOptions(siteId));
  },
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const { isSmallScreen } = useScreenSize();
  const hasElevatedAccess = useHasElevatedAccess();

  if (!siteId) return <div>Please select a site first</div>;

  return (
    <Suspense fallback={<LoadingFallback />}>
      {/* The grid lives in the index route; all other children are full pages. */}
      <Outlet />
      <UnsupportedScreenModal open={isSmallScreen && !hasElevatedAccess} />
    </Suspense>
  );
}
